import type {
  AudienceListTableEntry,
  PerformanceLists,
} from 'src/types/audience-list';
import type {SalesPerformanceEntry} from 'src/types/sales-performance-analytics';
import type {AtsMappingEntry} from 'src/types/analytics-ats-mapping';


type TranslationFunction = (key: string, value: string) => string;

type PerformanceListTooltipTexts = {
  current_placements: string;
  completed_placements: string;
  current_applications_or_submissions: string;
  client_submissions_roi: string;
  active_jobs_roi: string;
  candidates_with_active_placements: string;
  shortlist_post_screening: string;
  hired_post_screening: string;
  applicants: string;
  client_submissions: string;
  placements: string;
};

type PerformanceSalesListTooltipTexts = {
  gross_margin_per_placement: string;
  gross_revenue_per_placement: string;
  job_spend_per_candidate: string;
};

export const PERFORMANCE_TYPES = {
  candidates: 'candidates',
  sales: 'sales',
} as const;

export const ROI_SETUP_GATE_NAMES = [
  'current_applications_or_submissions',
  'client_submissions_roi',
  'active_jobs_roi',
] as const;

const performanceLists: PerformanceLists[] = [
  {
    name: 'current_placements',
    standardEntityType: 'placement',
    key: 'current_placements',
    type: PERFORMANCE_TYPES.candidates,
  },
  {
    name: 'completed_placements',
    standardEntityType: 'placement',
    key: 'completed_placements',
    type: PERFORMANCE_TYPES.candidates,
  },
  {
    name: 'current_applications_or_submissions',
    standardEntityType: 'job_submission',
    key: 'current_applications_or_submissions',
    type: PERFORMANCE_TYPES.candidates,
  },
  {
    name: 'client_submissions_roi',
    key: 'client_submissions_roi',
    type: PERFORMANCE_TYPES.candidates,
  },
  {
    name: 'active_jobs_roi',
    key: 'active_jobs_roi',
    type: PERFORMANCE_TYPES.candidates,
  },
  {
    name: 'candidates_with_active_placements',
    standardEntityType: 'candidate',
    key: 'candidates_with_active_placements',
    type: PERFORMANCE_TYPES.candidates,
  },
  {
    name: 'shortlist_post_screening',
    standardEntityType: 'job_submission',
    key: 'shortlist_post_screening',
    type: PERFORMANCE_TYPES.candidates,
  },
  {
    name: 'hired_post_screening',
    standardEntityType: 'placement',
    key: 'hired_post_screening',
    type: PERFORMANCE_TYPES.candidates,
  },
];

// Maps performance list name to the API list_name when they differ
const API_LIST_NAME_MAP: Record<string, string> = {
  current_applications_or_submissions: 'all_applicants',
  client_submissions_roi: 'client_submissions',
  active_jobs_roi: 'active_jobs',
};

export const getPerformanceLists = (
  lists: AudienceListTableEntry[],
  atsMappings: AtsMappingEntry[] = [],
): PerformanceLists[] => {
  // Build entity type map from API response
  const entityTypeMap = new Map<string, string>();
  atsMappings.forEach((mapping) => {
    if (mapping.sense_entity) {
      entityTypeMap.set(mapping.list_name, mapping.sense_entity);
    }
  });

  // Create a Map for quick lookups based on name
  const listMap = new Map<string, AudienceListTableEntry>();
  lists.forEach((list) => {
    if (list.name) {
      listMap.set(list.name, list);
    }
  });

  return performanceLists.map((performanceList) => {
    const list = listMap.get(performanceList.name);
    const apiListName =
      API_LIST_NAME_MAP[performanceList.name] ?? performanceList.name;
    const apiEntityType = entityTypeMap.get(apiListName);
    return {
      ...performanceList,
      ...(apiEntityType ? {standardEntityType: apiEntityType} : {}),
      ...(list ? {id: list.id, lastUserAction: list.lastUserActionAt} : {}),
    };
  }) as PerformanceLists[];
};

export const performanceListTooltipTexts = (
  t: TranslationFunction,
): PerformanceListTooltipTexts => ({
  current_applications_or_submissions: `${t(
    'Applicants_are_all_the_applications_to_your_jobs',
    'Applicants are all the applications to your jobs.',
  )} ${t(
    'Common_ways_to_track_this_is_to_use_a_status',
    'Common ways to track this is to use a status.',
  )}`,
  current_placements: `${t(
    'Current_placements_are_all_placements_that_are_billing_and_valid',
    'Current placements are all placements that are billing and valid.',
  )} ${t(
    'This_is_used_to_calculate_performance_metrics_such_as_redeployment_so_you_should_filter_it_down_only_to_contractors_you_want_to_track_in_that_way',
    'This is used to calculate performance metrics such as re-deployment, so you should filter it down only to contractors you want to track in that way.',
  )} ${t(
    'Common_ways_to_track_this_include_a_placement_status',
    'Common ways to track this include a placement status.',
  )} ${t(
    'This_audience_must_include_a_placement_field_in_the_rules',
    'This audience must include a placement field in the rules.',
  )}`,
  completed_placements: `${t(
    'Completed_placements_are_placements_that_have_ended',
    'Completed placements are placements that have ended.',
  )} ${t(
    'Common_ways_to_track_these_are_by_a_placement_status_or_end_date',
    'Common ways to track these are by a placement status or end date.',
  )} ${t(
    'This_audience_must_include_a_placement_field_in_the_rules',
    'This audience must include a placement field in the rules.',
  )}`,
  client_submissions_roi: `${t(
    'Client_submissions_are_all_the_submissions_to_your_jobs',
    'Client submissions are all the submissions to your jobs.',
  )} ${t(
    'Common_ways_to_track_this_is_to_use_a_status',
    'Common ways to track this is to use a status.',
  )}`,
  active_jobs_roi: `${t(
    'Active_jobs_are_all_the_currently_open_jobs',
    'Active jobs are all the currently open jobs.',
  )}`,
  candidates_with_active_placements: `${t(
    'Candidates_with_active_placements_are_all_candidates_who_currently_have_active_placements',
    'Candidates with active placements are all candidates who currently have active placements.',
  )}`,
  shortlist_post_screening: `${t(
    'Shortlist_post_screening_are_all_submissions_that_have_been_shortlisted_after_screening',
    'Shortlist post screening are all submissions that have been shortlisted after screening.',
  )}`,
  hired_post_screening: `${t(
    'Hired_post_screening_are_all_placements_that_have_been_hired_after_screening',
    'Hired post screening are all placements that have been hired after screening.',
  )}`,
  applicants: `${t(
    'Set_up_the_applicants_where_the_source_of_the_applicant_is_Sense_Discover',
    'Set up the applicants where the source of the applicant is Sense Discover.',
  )}`,
  client_submissions: `${t(
    'Set_up_the_client_submissions_where_the_source_of_the_applicant_is_Sense_Discover',
    'Set up the client submissions where the source of the applicant is Sense Discover.',
  )}`,
  placements: `${t(
    'Set_up_the_applicants_where_the_source_of_the_hire_or_placement_is_Sense_Discover',
    'Set up the applicants where the source of the hire or placement is Sense Discover.',
  )}`,
});

export const getPerformanceListDisplayName = (
  str: string | null | undefined = '',
  t: TranslationFunction,
): string => {
  // @ts-expect-error - TS18047 - 'str' is possibly 'null'.
  const words: string[] = str.split('_');

  for (let i: number = 0; i < words.length; i++) {
    words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
  }
  let displayName = words.join(' ');

  if (displayName === 'Current Placements') {
    displayName = t('Current_Placements', 'Current Placements');
  } else if (displayName === 'Completed Placements') {
    displayName = t('Completed_Placements', 'Completed Placements');
  } else if (displayName === 'Current Applications Or Submissions') {
    displayName = t('All_Applicants', 'All Applicants');
  } else if (displayName === 'Client Submissions Roi') {
    displayName = t('Client_Submissions', 'Client Submissions');
  } else if (displayName === 'Active Jobs Roi') {
    displayName = t('Active_Jobs', 'Active Jobs');
  } else if (displayName === 'Candidates With Active Placements') {
    displayName = t(
      'Candidates_With_Active_Placements',
      'Candidates With Active Placements',
    );
  } else if (displayName === 'Shortlist Post Screening') {
    displayName = t('Shortlist_Post_Screening', 'Shortlist Post Screening');
  } else if (displayName === 'Hired Post Screening') {
    displayName = t('Hired_Post_Screening', 'Hired Post Screening');
  } else if (displayName === 'Gross Margin Per Placement') {
    displayName = t('Gross_Margin_Per_Placement', 'Gross Margin Per Placement');
  } else if (displayName === 'Gross Revenue Per Placement') {
    displayName = t(
      'Gross_Revenue_Per_Placement',
      'Gross Revenue Per Placement',
    );
  } else if (displayName === 'Job Board Spend Per Candidate') {
    displayName = t(
      'Job_Board_Spend_Per_Candidate',
      'Job Board Spend Per Candidate',
    );
  }
  return displayName;
};

const salesPerformanceLists: PerformanceLists[] = [
  {
    name: 'gross_margin_per_placement',
    key: 'gross_margin_per_placement',
    standardEntityType: 'placement',
    type: PERFORMANCE_TYPES.sales,
    currencyName: 'USD',
  },
  {
    name: 'gross_revenue_per_placement',
    key: 'gross_revenue_per_placement',
    standardEntityType: 'placement',
    type: PERFORMANCE_TYPES.sales,
    currencyName: 'USD',
  },
  {
    name: 'job_board_spend_per_candidate',
    key: 'job_spend_per_candidate',
    standardEntityType: 'placement',
    type: PERFORMANCE_TYPES.sales,
    currencyName: 'USD',
  },
];

export const getSalesPerformanceLists = (
  lists: SalesPerformanceEntry[],
): PerformanceLists[] => {
  // Create a Map for quick lookups based on name and standardEntityType
  const listMap = new Map<string, SalesPerformanceEntry>();
  lists.forEach((list) => {
    const key = list.name;
    listMap.set(key, list);
  });
  // Use map to create a new array based on the conditions
  const mappedLists = salesPerformanceLists.map((salesPerformance) => {
    const key = salesPerformance.key;
    const list = listMap.get(key);

    if (list) {
      return {
        ...salesPerformance,
        id: list.id,
        lastUserAction: list.time_updated,
        currencyName: list.currency_name,
        currencyValue: list.currency_value,
        type: PERFORMANCE_TYPES.sales,
      };
    } else {
      return salesPerformance;
    }
  });
  return mappedLists;
};

const discoverJobSearchPerformanceLists: PerformanceLists[] = [
  {
    name: 'applicants',
    standardEntityType: 'job_submission',
    key: 'applicants',
    type: PERFORMANCE_TYPES.candidates,
  },
  {
    name: 'client_submissions',
    standardEntityType: 'job_submission',
    key: 'client_submissions',
    type: PERFORMANCE_TYPES.candidates,
  },
  {
    name: 'placements',
    standardEntityType: 'placement',
    key: 'placements',
    type: PERFORMANCE_TYPES.candidates,
  },
];

export const getDiscoverJobSearchPerformanceLists = (
  lists: AudienceListTableEntry[],
): PerformanceLists[] => {
  // Create a Map for quick lookups based on name and standardEntityType
  const listMap = new Map<string, AudienceListTableEntry>();
  lists.forEach((list) => {
    const key = `${list.name}-${list.standardEntityType}`;
    listMap.set(key, list);
  });
  // Use map to create a new array based on the conditions
  const mappedLists = discoverJobSearchPerformanceLists.map((discoverList) => {
    const key = `${discoverList.name}-${discoverList.standardEntityType ?? ''}`;
    const list = listMap.get(key);
    if (list) {
      return {
        ...discoverList,
        id: list.id,
        lastUserAction: list.lastUserActionAt,
      };
    } else {
      return discoverList;
    }
  });

  return mappedLists;
};

export const performanceSalesListTooltipTexts = (
  t: TranslationFunction,
): PerformanceSalesListTooltipTexts => ({
  gross_margin_per_placement: t(
    'Gross_Margin_per_placement_is_an_average_gross_margin_your_staffing_agency_makes_per_placement',
    'Gross Margin per placement is an average gross margin your staffing agency makes per placement.',
  ),
  gross_revenue_per_placement: t(
    'Revenue_per_placement_is_an_average_revenue_your_staffing_agency_makes_per_placement',
    'Revenue per placement is an average revenue your staffing agency makes per placement.',
  ),
  job_spend_per_candidate: t(
    'Job_Board_spend_per_candidate_is_the_amount_youd_spend_on_job_board_per_candidate_sourced_from_there',
    'Job Board spend per candidate is the amount you’d spend on job board per candidate sourced from there.',
  ),
});

export const getPlaceholderTextForSales = (
  key: string,
  t: (key: string, defaultValue: string) => string,
): string => {
  const placeholders: {
    [key: string]: string;
  } = {
    gross_margin_per_placement: t(
      'Enter_gross_margin_per_placement',
      'Enter gross margin per placement',
    ),
    gross_revenue_per_placement: t(
      'Enter_gross_revenue_per_placement',
      'Enter gross revenue per placement',
    ),
    job_spend_per_candidate: t(
      'Enter_job_spend_per_candidate',
      'Enter job spend per candidate',
    ),
  };

  return placeholders[key] || t('Enter_value', 'Enter value');
};

export const formatNumberWithCommas = (number: number): string => {
  const formatter = new Intl.NumberFormat('en-US');
  return formatter.format(number);
};
